/**
 * JsonLooseFkBadges — renders one or more clickable chain-link badges on a
 * JSON cell when the source column hosts loose-FK declarations with a JSON
 * `sourcePath`. Each declaration becomes ONE chip; if a declaration's path
 * uses `[*]` and expands to N primitives, the chip is a count chip that
 * opens a dropdown listing one menu item per extracted value, each menu
 * item opening the existing `ForeignKeyPopover` for its value.
 *
 * Pass 3, Slice A: A single JSON column can have multiple loose FKs
 * (e.g. `$.customer_id → customers.id`, `$.warehouse_id → warehouses.id`).
 * This component renders N chips in declaration order, each labelled with
 * its path so the user can tell them apart at a glance.
 *
 * Wildcard-array UX: a wildcard declaration (e.g. `$[*].ArticleId` on a
 * root-array JSON cell) used to fan out into N inline chips, which made
 * long arrays explode the row height. The new shape is a single compact
 * "× N" chip that opens a dropdown of extracted values on click — each
 * dropdown row is its own `ForeignKeyPopover` trigger that opens the
 * existing "Related row from (loose)…" peek for the row's id.
 *
 * Layout: each chip caps the rendered path with `truncate max-w-[10rem]`
 * inside its inner flex so the value stays fully visible while a long path
 * collapses with an ellipsis. The outer wrapper is `flex-wrap min-w-0
 * overflow-hidden` so chips never leak past the cell's right edge in a
 * narrow column.
 */
import { useState } from 'react';
import { Link2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import { ForeignKeyPopover } from '../../../table-explorer/components/ForeignKeyPopover';
import {
  extractJsonPathScalar,
  extractJsonPathScalarsAll,
} from '../../utils/extractJsonPath';
import { hasJsonPathWildcard } from '../../utils/jsonFilterSql';
import type { LooseRelationship } from '../../../../../types/electron';

type JsonLooseFkBadgesProps = {
  connectionId: string;
  database: string;
  /** The JSON cell value (parsed object or JSON string). */
  value: unknown;
  /** Source-side coordinates for the popover's loose-FK identity. */
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  /**
   * Loose-FK declarations to surface. Callers filter to those with a
   * `sourcePath` so the badge always has a path label. Order is preserved.
   */
  rels: readonly LooseRelationship[];
  surface?: 'query_editor' | 'table_explorer';
};

type ChipScalar = string | number | boolean | null;

const CHIP_CLASS =
  'inline-flex items-center gap-0.5 px-1 max-w-full rounded border border-teal-500/40 text-teal-600 dark:text-teal-400 text-[10px] font-mono cursor-pointer hover:bg-teal-500/10';

export function JsonLooseFkBadges({
  connectionId,
  database,
  value,
  sourceSchema,
  sourceTable,
  sourceColumn,
  rels,
  surface,
}: JsonLooseFkBadgesProps) {
  if (rels.length === 0) return null;

  // One chip per declaration. Wildcards collapse the per-element fan-out
  // into a single dropdown chip; non-wildcards stay as direct FK-popover
  // triggers (the existing UX, since there's only one value to peek at).
  const chipNodes = rels
    .map((rel) => {
      const path = rel.sourcePath;
      if (!path) return null;
      if (hasJsonPathWildcard(path)) {
        const scalars = extractJsonPathScalarsAll(value, path);
        // No extracted primitives → don't render a chip at all (empty
        // arrays / non-primitive expansions would mislead the user).
        if (scalars.length === 0) return null;
        return (
          <JsonLooseFkChipDropdown
            key={rel.id}
            connectionId={connectionId}
            database={database}
            rel={rel}
            path={path}
            scalars={scalars}
            sourceSchema={sourceSchema}
            sourceTable={sourceTable}
            sourceColumn={sourceColumn}
            surface={surface}
          />
        );
      }
      const extracted = extractJsonPathScalar(value, path);
      const scalar: ChipScalar = extracted === undefined ? null : extracted;
      return (
        <JsonLooseFkSingleChip
          key={rel.id}
          connectionId={connectionId}
          database={database}
          rel={rel}
          path={path}
          scalar={scalar}
          sourceSchema={sourceSchema}
          sourceTable={sourceTable}
          sourceColumn={sourceColumn}
          surface={surface}
        />
      );
    })
    .filter((node): node is JSX.Element => node !== null);

  if (chipNodes.length === 0) return null;

  return (
    <span
      className="inline-flex flex-wrap items-center gap-1 min-w-0 overflow-hidden"
      data-testid="loose-fk-json-badges"
    >
      {chipNodes}
    </span>
  );
}

type SingleChipProps = {
  connectionId: string;
  database: string;
  rel: LooseRelationship;
  path: string;
  scalar: ChipScalar;
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  surface?: 'query_editor' | 'table_explorer';
};

function JsonLooseFkSingleChip({
  connectionId,
  database,
  rel,
  path,
  scalar,
  sourceSchema,
  sourceTable,
  sourceColumn,
  surface,
}: SingleChipProps) {
  const displayValue = scalar === null ? '∅' : String(scalar);
  // ∅ means "path didn't resolve to a primitive" — the popover would just
  // report "No matching row found", so render a non-clickable chip instead.
  if (displayValue === '∅') {
    return (
      <span
        className={CHIP_CLASS + ' opacity-70 cursor-default'}
        data-testid={`loose-fk-json-badge-${path}`}
        title={`${path} → ${rel.toSchema}.${rel.toTable}.${rel.toColumn}`}
      >
        <Link2 className="h-2.5 w-2.5 opacity-70 flex-shrink-0" aria-hidden />
        <span className="truncate max-w-[10rem]">{path}</span>
        <span className="text-muted-foreground/70 flex-shrink-0">:</span>
        <span className="flex-shrink-0">∅</span>
      </span>
    );
  }
  return (
    <ForeignKeyPopover
      connectionId={connectionId}
      database={database}
      referencedSchema={rel.toSchema}
      referencedTable={rel.toTable}
      referencedColumn={rel.toColumn}
      value={scalar}
      isLoose
      sourceSchema={sourceSchema}
      sourceTable={sourceTable}
      sourceColumn={sourceColumn}
      surface={surface}
    >
      <span
        className={CHIP_CLASS}
        data-testid={`loose-fk-json-badge-${path}`}
        title={`${path} → ${rel.toSchema}.${rel.toTable}.${rel.toColumn} (${displayValue})`}
      >
        <Link2 className="h-2.5 w-2.5 opacity-70 flex-shrink-0" aria-hidden />
        <span className="truncate max-w-[10rem]">{path}</span>
        <span className="text-muted-foreground/70 flex-shrink-0">:</span>
        <span className="flex-shrink-0">{displayValue}</span>
      </span>
    </ForeignKeyPopover>
  );
}

type DropdownChipProps = {
  connectionId: string;
  database: string;
  rel: LooseRelationship;
  path: string;
  scalars: ChipScalar[];
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  surface?: 'query_editor' | 'table_explorer';
};

/**
 * Wildcard-path chip: a single compact "× N" trigger that opens a popover
 * dropdown listing the extracted primitive at each array position. Each
 * dropdown row is itself a `ForeignKeyPopover` trigger so clicking it opens
 * the standard FK peek for that value.
 */
function JsonLooseFkChipDropdown({
  connectionId,
  database,
  rel,
  path,
  scalars,
  sourceSchema,
  sourceTable,
  sourceColumn,
  surface,
}: DropdownChipProps) {
  const [open, setOpen] = useState(false);
  const count = scalars.length;
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={CHIP_CLASS}
          data-testid={`loose-fk-json-badge-${path}`}
          title={`${path} → ${rel.toSchema}.${rel.toTable}.${rel.toColumn} (${count} value${count === 1 ? '' : 's'})`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Link2 className="h-2.5 w-2.5 opacity-70 flex-shrink-0" aria-hidden />
          <span className="truncate max-w-[10rem]">{path}</span>
          <span className="text-muted-foreground/70 flex-shrink-0">×</span>
          <span className="flex-shrink-0">{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-1"
        align="start"
        data-testid={`loose-fk-json-menu-${path}`}
      >
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pb-1 pt-0.5">
          {count} loose FK record{count === 1 ? '' : 's'}
        </div>
        <div className="flex flex-col">
          {scalars.map((scalar, i) => {
            const itemValue = scalar === null ? '∅' : String(scalar);
            // ∅ rows can't open a meaningful popover (no lookup key), so
            // skip them — wildcard extraction already filters non-primitives,
            // but defensive in case a future extractor surfaces nulls.
            if (scalar === null) {
              return (
                <span
                  key={i}
                  className="px-2 py-1 text-xs font-mono text-muted-foreground/70"
                  data-testid={`loose-fk-json-menu-item-${path}-${i}`}
                >
                  ∅
                </span>
              );
            }
            return (
              <ForeignKeyPopover
                key={i}
                connectionId={connectionId}
                database={database}
                referencedSchema={rel.toSchema}
                referencedTable={rel.toTable}
                referencedColumn={rel.toColumn}
                value={scalar}
                isLoose
                sourceSchema={sourceSchema}
                sourceTable={sourceTable}
                sourceColumn={sourceColumn}
                surface={surface}
              >
                <span
                  className="px-2 py-1 text-xs font-mono rounded hover:bg-muted cursor-pointer flex items-center gap-1"
                  data-testid={`loose-fk-json-menu-item-${path}-${i}`}
                  title={`Open ${rel.toSchema}.${rel.toTable}.${rel.toColumn} = ${itemValue}`}
                >
                  <Link2 className="h-2.5 w-2.5 opacity-60 flex-shrink-0" aria-hidden />
                  <span className="flex-1 truncate">{itemValue}</span>
                </span>
              </ForeignKeyPopover>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
